// Vue
import { defineComponent, PropType } from "vue";

// Features
import { InspectEmissionByStagesType } from "@/features/Inspect";
import { ChartWrapper, VerticalBarChart, verticalBarChartProps, ChartTooltipEventType } from "@/features/Charts";

import { useTooltip } from "@stores/useTooltip";

// Utils
import format from "@/services/Utils/format";

const EmissionByMaterialLifecycleStageChartComponent = defineComponent({
    props: {
        data: {
            type: Object as PropType<InspectEmissionByStagesType>,
            default: () => ({}),
        },
        unit: verticalBarChartProps.unit,
        title: {
            type: String,
            default: "Emission by Material Lifecycle Stage",
        },
        settings: {
            type: Object as PropType<{
                realtimeSort?: boolean;
                inverse?: boolean;
                rotateLabels?: 45 | 90 | boolean;
                legend?: boolean;
                stackedCategories?: string[];
            }>,
            default: () => ({}),
        },
    },

    setup(props) {
        const tooltip = useTooltip();

        const stages = [
            { label: "A1-A3 - Production", value: "A1A3" },
            { label: "A4 - Transport", value: "A4" },
            { label: "A5 - Installation", value: "A5" },
            { label: "B1 - Use", value: "B1" },
            { label: "B2 - Maintenance", value: "B2" },
            { label: "B3 - Repair", value: "B3" },
            { label: "B4 - Replacement", value: "B4" },
            { label: "B5 - Refurbishment", value: "B5" },
            { label: "B6 - Operational Energy Use", value: "B6" },
            { label: "B7 - Operational Water User", value: "B7" },
            { label: "C1 - Demolition", value: "C1" },
            { label: "C2 - Transport", value: "C2" },
            { label: "C3 - Waste processing", value: "C3" },
            { label: "C4 - Disposal", value: "C4" },
            { label: "D - Reuse, recovery, recycling", value: "D" },
        ];

        const getStageLabel = (stageValue: string) => stages.find((stage) => stage.value === stageValue)?.label || stageValue;

        const formatTooltipText = ({ event, unit }: { event: ChartTooltipEventType; unit: string }) => {
            return `
                <div class="flex flex-col text-white">
                    <div class="mb-px-6 h-px-8 w-px-24 rounded-4" style="background-color:${event?.color || "transparent"}"></div>
                    <span class="t-xs">${getStageLabel(event?.name as string)}</span>
                    <span class="t-caption-bold">${event?.seriesName ?? ""}</span>
                    <span class="t-caption">${format.decimalNumber(event?.value as number)} ${unit}</span>
                </div>
            `;
        };

        const onHover = (event: ChartTooltipEventType) => {
            tooltip.showTooltip(formatTooltipText({ event, unit: props.unit }));
        };

        const onLeave = () => {
            tooltip.hideTooltip();
        };

        return {
            props,
            onHover,
            onLeave,
        };
    },

    render() {
        const { props, onHover, onLeave } = this;

        return (
            <div class="flex flex-col space-y-px-12">
                <ChartWrapper
                    title={props.title}
                    height={600}
                    marginBottom={0}>
                    <VerticalBarChart
                        data={props.data}
                        unit={props.unit}
                        settings={{ stacked: true, ...props.settings }}
                        onHover={onHover}
                        onLeave={onLeave}
                    />
                </ChartWrapper>
            </div>
        );
    },
});

export default EmissionByMaterialLifecycleStageChartComponent;
